import { getMaxSearchDate, isOutsideSearchWindow, withFieldGroup } from '@massilia/features'
import { Button, cn, useIsMobile } from '@massilia/ui'
import { ArrowLeftRight, ArrowUpDown, X } from 'lucide-react'
import { useState } from 'react'

import type { FlightLegType } from '@/lib/schemas'

const FIELD_LABEL = 'text-gray-light-600 text-[11px] font-semibold tracking-wide uppercase'
const BARE_TRIGGER =
  'h-auto w-full justify-start border-0 bg-transparent px-0 py-0 text-base shadow-none hover:bg-transparent focus-visible:ring-0'

export const FlightLegRowFormGroup = withFieldGroup({
  defaultValues: {} as FlightLegType,
  props: {
    dateType: 'range',
    errorPopoverSide: 'bottom',
    errorsDisplayType: 'popover',
    multiLeg: false,
  } as {
    dateType: 'range' | 'single'
    disabledDateFn?: (date: Date) => boolean
    errorPopoverSide?: 'bottom' | 'top'
    errorsDisplayType?: 'bellow' | 'popover'
    /** Render as a multi-destination leg (card on mobile, reserved remove slot on desktop). */
    multiLeg?: boolean
    /** When provided, the leg shows a remove (X) button in a reserved, shift-free slot. */
    onRemove?: () => void
  },
  render: function Render({
    dateType,
    disabledDateFn,
    errorPopoverSide = 'bottom',
    errorsDisplayType = 'popover',
    group,
    multiLeg = false,
    onRemove,
  }) {
    const isMobile = useIsMobile()
    const [isRotated, setIsRotated] = useState(false)
    const maxSearchDate = getMaxSearchDate()

    const swapAirports = () => {
      const departure = group.getFieldValue('departure')
      const destination = group.getFieldValue('destination')
      group.setFieldValue('departure', destination)
      group.setFieldValue('destination', departure)
      setIsRotated(!isRotated)
    }

    const dateField =
      dateType === 'range' ? (
        <group.AppField name="date">
          {(field) => (
            <field.MultiDateField
              disabled={isOutsideSearchWindow}
              endMonth={maxSearchDate}
              errorsDisplayType={errorsDisplayType}
              overlayOnMobile
            />
          )}
        </group.AppField>
      ) : (
        <group.AppField name="date.from">
          {(field) => (
            <field.CalendarField
              disabled={disabledDateFn}
              endMonth={maxSearchDate}
              errorsDisplayType={errorsDisplayType}
              overlayOnMobile
            />
          )}
        </group.AppField>
      )

    // Mobile, multi-destination: each leg is one card (Départ / Arrivée / Date
    // stacked, booking.com style) with the remove X pinned in a reserved corner.
    if (isMobile && multiLeg) {
      return (
        <div className="border-input relative rounded-xl border bg-white">
          <div className="flex flex-col gap-0.5 py-2.5 pr-12 pl-4">
            <span className={FIELD_LABEL}>Départ</span>
            <group.AppField name="departure">
              {(field) => (
                <field.AirportAutocompleteField
                  errorPopoverSide={errorPopoverSide}
                  errorsDisplayType={errorsDisplayType}
                  mode="departure"
                  triggerClassName={BARE_TRIGGER}
                />
              )}
            </group.AppField>
          </div>

          <div className="border-input border-t" />

          <div className="flex flex-col gap-0.5 px-4 py-2.5">
            <span className={FIELD_LABEL}>Arrivée</span>
            <group.AppField name="destination">
              {(field) => (
                <field.AirportAutocompleteField
                  errorPopoverSide={errorPopoverSide}
                  errorsDisplayType={errorsDisplayType}
                  mode="destination"
                  triggerClassName={BARE_TRIGGER}
                />
              )}
            </group.AppField>
          </div>

          <div className="border-input border-t" />

          <div className="flex flex-col gap-0.5 px-4 py-2.5">
            <span className={FIELD_LABEL}>Date</span>
            <group.AppField name="date.from">
              {(field) => (
                <field.CalendarField
                  className={BARE_TRIGGER}
                  disabled={disabledDateFn}
                  endMonth={maxSearchDate}
                  errorsDisplayType={errorsDisplayType}
                  overlayOnMobile
                />
              )}
            </group.AppField>
          </div>

          {onRemove && (
            <Button
              aria-label="Supprimer ce vol"
              className="text-gray-light-500 absolute top-2 right-2 size-8 rounded-full p-0 hover:bg-gray-100 hover:text-gray-900"
              onClick={onRemove}
              type="button"
              variant="ghost"
            >
              <X className="size-4" />
            </Button>
          )}
        </div>
      )
    }

    // Single-leg mobile (one-way / round-trip): connected From/To box with a
    // vertical swap on the divider, then a labeled date section.
    if (isMobile) {
      return (
        <div className="flex w-full flex-col gap-3">
          <div className="border-input relative rounded-xl border bg-white">
            <div className="flex flex-col gap-0.5 py-2.5 pr-14 pl-4">
              <span className={FIELD_LABEL}>Départ</span>
              <group.AppField name="departure">
                {(field) => (
                  <field.AirportAutocompleteField
                    errorPopoverSide={errorPopoverSide}
                    errorsDisplayType={errorsDisplayType}
                    mode="departure"
                    triggerClassName={BARE_TRIGGER}
                  />
                )}
              </group.AppField>
            </div>

            <div className="border-input border-t" />

            <div className="flex flex-col gap-0.5 py-2.5 pr-14 pl-4">
              <span className={FIELD_LABEL}>Arrivée</span>
              <group.AppField name="destination">
                {(field) => (
                  <field.AirportAutocompleteField
                    errorPopoverSide={errorPopoverSide}
                    errorsDisplayType={errorsDisplayType}
                    mode="destination"
                    triggerClassName={BARE_TRIGGER}
                  />
                )}
              </group.AppField>
            </div>

            <Button
              aria-label="Inverser départ et arrivée"
              className="border-gray-light-300 absolute top-1/2 right-3 size-9 -translate-y-1/2 rounded-full border bg-white p-0 shadow-sm hover:bg-gray-50"
              onClick={swapAirports}
              type="button"
              variant="ghost"
            >
              <ArrowUpDown
                className={cn('text-brand-600 size-4 transition-transform duration-300', {
                  'rotate-180': isRotated,
                })}
              />
            </Button>
          </div>

          <div className="flex flex-col gap-1 [&>div>button]:h-14 [&>div>button]:w-full">
            <span className={FIELD_LABEL}>{dateType === 'range' ? 'Dates' : 'Date'}</span>
            {dateField}
          </div>
        </div>
      )
    }

    const desktopContent = (
      <div className="flex w-full flex-1 flex-col flex-wrap gap-2 md:grid md:grid-cols-[3fr_1fr]">
        <div className="relative flex flex-1 flex-col gap-3 md:flex-row">
          <div className="flex-1">
            <group.AppField name="departure">
              {(field) => (
                <field.AirportAutocompleteField
                  errorPopoverSide={errorPopoverSide}
                  errorsDisplayType={errorsDisplayType}
                  mode="departure"
                />
              )}
            </group.AppField>
          </div>

          <Button
            className={cn(
              'border-gray-light-300 absolute top-1/2 left-1/2 z-20 rounded-full border bg-white'
            )}
            onClick={swapAirports}
            type="button"
            variant="ghost"
            style={{ transform: 'translate(-50%, -50%)' }}
          >
            <ArrowLeftRight
              className={cn('text-brand-600 size-4 transition-transform duration-300', {
                'rotate-180': isRotated,
              })}
            />
          </Button>

          <div className="flex-1">
            <group.AppField name="destination">
              {(field) => (
                <field.AirportAutocompleteField
                  errorPopoverSide={errorPopoverSide}
                  errorsDisplayType={errorsDisplayType}
                  mode="destination"
                />
              )}
            </group.AppField>
          </div>
        </div>

        {dateField}
      </div>
    )

    // Desktop, multi-destination: keep the one-row layout, but give the remove X
    // a fixed-width reserved slot so toggling it never resizes the fields.
    if (multiLeg) {
      return (
        <div className="flex w-full flex-row items-start gap-2">
          <div className="min-w-0 flex-1">{desktopContent}</div>
          <div className="flex w-9 shrink-0 justify-center pt-1">
            {onRemove && (
              <Button
                aria-label="Supprimer ce vol"
                className="text-gray-light-500 size-9 rounded-full p-0 hover:bg-gray-100 hover:text-gray-900"
                onClick={onRemove}
                type="button"
                variant="ghost"
              >
                <X className="size-4" />
              </Button>
            )}
          </div>
        </div>
      )
    }

    return desktopContent
  },
})
